<template>
  <div class="week-reservations">
    <div class="filters">
      <input
        type="text"
        v-model="filters.search"
        :placeholder="$t('dashboardReservations.searchClients')" />

      <span
        v-if="workersList.length"
        class="separator"></span>

      <select
        v-if="workersList.length && !userIsWorker"
        v-model="filters.worker">
        <option key="worker-wld-00" value="">{{ $t('common.ui.allWorkers') }}</option>
        <option
          v-for="workerData of workersList"
          :key="'worker-wld-' + workerData.id"
          :value="workerData.id">
          {{ workerData.name }}
        </option>
      </select>

      <span
        v-if="servicesList.length"
        class="separator"></span>

      <select
        v-if="servicesList.length"
        v-model="filters.service">
        <option key="services-list-group-00" value="">{{ $t('common.ui.allServices') }}</option>
        <template
          v-for="(servicesCategory, index) of servicesCategories"
          v-if="servicesInCategory(servicesCategory.id).length">
          <option :key="'services-list-group-' + index" :value="'c' + servicesCategory.id">
            {{ servicesCategory.name }}
          </option>
          <option
            v-for="(service, subindex) of servicesInCategory(servicesCategory.id)"
            :value="service.id"
            :key="'services-list-group-' + index + '-' + subindex">
            &mdash; {{ service.name }}
          </option>
        </template>
      </select>

      <span class="separator"></span>

      <btn
        @click.native="clearFilters"
        type="danger with-icon"
        iconName="cross"
        iconSize="xxxs"
        iconColor="color-light"
        :disabled="!filterIsActive">
        {{ $t('common.ui.clearFilters') }}
      </btn>
    </div>

    <message
      v-if="lastUpdate && lastDataDownload && lastUpdate > lastDataDownload"
      type="info">
      {{ $t('dashboard.updateRequired') }}
      <a
        v-if="!dataAreLoading"
        href="#"
        @click.prevent="debouncedLoadCounters">
        {{ $t('dashboard.updateData') }}
      </a>
      <a
        v-else
        href="#"
        @click.prevent="false">
        {{ $t('dashboard.updatingData') }}
      </a>
    </message>

    <div class="week-reservations-block">
      <h3>{{ $t('dashboardReservations.reservationsForUpcomingWeek') }}</h3>

      <div class="week-reservations-days">
        <div
          v-for="(day, index) of weekDaysLabels"
          :key="'week-reservations-days-' + index"
          :class="{
            'select-days-desktop': true,
            'week-reservations-days-item': true,
            'is-active': currentWeekDay === day
          }"
          @click="changeWeekDay(day)">
          {{ day }}
          <counter
            :value="weekDays[day]"
            :moreContrast="!!weekDays[day]" />
        </div>

        <div class="select-days-mobile">
          <label>
            {{ $t('dashboardReservations.selectDay') }}:
          </label>
          <select
            v-model="mobileCurrentWeekDay">
            <option
              v-for="(day, index) of weekDaysLabels"
              :key="'week-reservations-days-option-' + index"
              :value="day">
              {{ day }} ({{ weekDays[day] }} {{ reservationWordVariations(weekDays[day]) }})
            </option>
          </select>
        </div>
      </div>

      <div
        v-if="reservations.length"
        :class="{
          'week-reservations-list': true,
          'is-loading': dataAreLoading
        }">
        <reservation
          v-for="(reservationData, index) in reservations"
          :reservationData="reservationData"
          :key="'trl-' + index"
          :isReservation="true"
          :isTabbedView="true"
          :isBlock="isMobile || isTablet"
          :reservationHoursFirst="true" />
      </div>
    </div>

    <pagination
      v-if="paginationMeta && reservations.length"
      ref="pagination"
      :max="paginationMeta.last_page"
      :current="paginationCurrent"
      :meta="paginationMeta"
      :onChange="updatePagination"
      :activeFilters="filterIsActive" />

    <div
      v-if="!reservationsLoaded"
      class="week-reservations-empty-state">
      {{ $t('dashboardReservations.loadingReservations') }}
    </div>

    <div
      v-if="paginationMeta && !paginationMeta.total && reservationsLoaded"
      class="week-reservations-empty-state">
      <template v-if="!filterIsActive">{{ $t('dashboardReservations.noUpcomingReservations') }}</template>
      <template v-if="filterIsActive">{{ $t('dashboardReservations.noUpcoimngReservationsForGivenFilters') }}</template>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import Configuration from './../mixins/Configuration';
import CalendarUtils from './../helpers/CalendarUtils';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Reservation from './Reservation';
import Pagination from './../common/Pagination';
import Responsive from './../mixins/Responsive';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'dashboard-week-reservations',
  mixins: [
    Configuration,
    Responsive,
    WordVariations,
    UserTypes
  ],
  props: [
    'currentWeekDay',
    'services',
    'servicesCategories',
    'servicesLoaded',
    'workers',
    'workersLoaded'
  ],
  components: {
    'pagination': Pagination,
    'reservation': Reservation
  },
  data () {
    return {
      filters: {
        worker: '',
        service: '',
        search: ''
      },
      weekDays: {},
      reservations: [],
      reservationsLoaded: false,
      paginationMeta: null,
      paginationCurrent: 1,
      dataAreLoading: false,
      mobileCurrentWeekDay: false,
      lastUpdate: null,
      lastDataDownload: null
    }
  },
  computed: {
    filterIsActive () {
      return this.filters.worker !== '' || this.filters.service !== '' || this.filters.search !== '';
    },
    servicesList () {
      return this.services.map(service => ({
        id: service.id,
        name: service.name,
        category_id: service.category_id
      }));
    },
    workersList () {
      return this.workers.filter(worker => worker.worker_type === 0).map(worker => ({
        id: worker.id,
        name: worker.name
      }));
    },
    weekDaysLabels () {
      return Object.keys(this.weekDays);
    }
  },
  watch: {
    'filters': {
      handler: function () {
        this.dataAreLoading = true;
        this.debouncedLoadReservations();

        if (this.$refs.pagination) {
          this.paginationCurrent = 1;
          this.$refs.pagination.reset();
        }
      },
      deep: true
    },
    'mobileCurrentWeekDay': function (newValue, oldValue) {
      if (newValue !== false && oldValue !== false) {
        this.dataAreLoading = true;
        this.$bus.$emit('dashboard-update-week-current-day', newValue);
      }
    }
  },
  mounted () {
    this.loadCounters();
    this.$bus.$on('dashboard-reservations-list-reload-needed', this.debouncedLoadCounters);
    this.$bus.$on('dashboard-reservations-list-tab-week-needs-update', this.updateRequired);
    this.$bus.$on('dashboard-reservations-list-week-day-change', this.debouncedLoadReservationsDayChange);
  },
  methods: {
    dayLabel (dateItem, day) {
      return CalendarUtils.humanDateFormat(dateItem, false) + ' - ' + day + ':';
    },
    clearFilters () {
      this.filters.worker = '';
      this.filters.service = '';
      this.filters.search = '';

      if (this.$refs.pagination) {
        this.paginationCurrent = 1;
        this.$refs.pagination.reset();
      }
    },
    debouncedLoadCounters: debounce(function () {
      this.loadCounters();
    }, 500),
    debouncedLoadReservations: debounce(function () {
      this.loadReservations();
    }, 500),
    debouncedLoadReservationsDayChange: debounce(function () {
      this.loadReservations(true);
    }, 500),
    loadCounters () {
      this.dataAreLoading = true;
      let queryString = {};

      if (this.filters.worker) {
        queryString.worker_id = this.filters.worker;
      }

      if (this.filters.service) {
        queryString.service_id = this.filters.service;
      }

      if (this.filters.search) {
        queryString.search = this.filters.search;
      }

      this.$http.get('/panel/stats/stats-dashboard-week-count', { params: queryString }).then(response => {
        this.weekDays = response.data;

        if (!this.currentWeekDay) {
          let firstNonEmptyDay = this.weekDaysLabels[0];

          for (let i = 0; i < this.weekDaysLabels.length; i++) {
            let day = this.weekDaysLabels[i];

            if (this.weekDays[day] > 0) {
              firstNonEmptyDay = day;
              break;
            }
          }

          this.$bus.$emit('dashboard-update-week-current-day', firstNonEmptyDay);
          this.mobileCurrentWeekDay = firstNonEmptyDay;
        } else {
          this.mobileCurrentWeekDay = this.currentWeekDay;
        }

        this.loadReservations();
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/stats/stats-dashboard-week-count', this.$router);
      });
    },
    loadReservations (dayChange = false) {
      this.dataAreLoading = true;

      let queryString = {
        page: this.paginationCurrent
      };

      if (this.filters.worker) {
        queryString.worker_id = this.filters.worker;
      }

      if (this.filters.service) {
        queryString.service_id = this.filters.service;
      }

      if (this.filters.search) {
        queryString.search = this.filters.search;
      }

      if (this.currentWeekDay) {
        queryString.selected_date = this.currentWeekDay;
      } else {
        this.dataAreLoading = false;
        return;
      }

      this.$http.get('/panel/calendar/get-week-reservations', { params: queryString }).then(response => {
        this.reservations = response.data.data;
        this.paginationMeta = response.data.meta;
        this.reservationsLoaded = true;
        this.dataAreLoading = false;
        this.lastDataDownload = +new Date();

        if (this.paginationMeta.last_page && this.paginationMeta.current_page > this.paginationMeta.last_page) {
          this.updatePagination(this.paginationMeta.last_page);
        }
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/calendar/get-week-reservations', this.$router);
        this.reservationsLoaded = true;
        this.dataAreLoading = false;
      });
    },
    updatePagination (nextPage) {
      this.dataAreLoading = true;
      this.paginationCurrent = parseInt(nextPage, 10);
      this.debouncedLoadReservations();
    },
    servicesInCategory (categoryID) {
      return this.servicesList.filter(service => service.category_id === categoryID);
    },
    changeWeekDay (day) {
      this.dataAreLoading = true;
      this.$bus.$emit('dashboard-update-week-current-day', day);
    },
    updateRequired () {
      console.log('UPDATE REQUIRED!');
      this.lastUpdate = +new Date();
    }
  },
  beforeDestroy () {
    this.$bus.$off('dashboard-reservations-list-reload-needed', this.debouncedLoadCounters);
    this.$bus.$off('dashboard-reservations-list-tab-week-needs-update', this.updateRequired);
    this.$bus.$off('dashboard-reservations-list-week-day-change', this.debouncedLoadReservationsDayChange);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.week-reservations {
  .select-days-mobile {
    display: none;
  }

  .select-days-desktop {
    display: block;
  }
  .week-reservations-days {
    border-top: 1px solid $color-border;
    display: flex;
    margin: 0 -2.5rem;

    &-item {
      background: $color-bg-light;
      border-left: 1px solid $color-border;
      cursor: pointer;
      padding: 2rem 1rem;
      text-align: center;
      width: 14.28%;

      &:first-child {
        border-left: none;
      }

      &.is-active {
        background: $color-bg-lightest;
        font-weight: bold;
      }
    }
  }

  &-block {
    background: $color-light;
    border: 1px solid $color-border;
    margin-bottom: 1rem;
    padding: 2.5rem 2.5rem 0 2.5rem;
  }

  &-empty-state {
    color: $color-text-light;
    padding: 4rem 0;
    text-align: center;
  }

  h3 {
    font-size: 2.4rem;
    font-weight: 400;
    line-height: 1;
    margin: 0 0 2.6rem 0;
  }

  &-list {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -2.6rem;
    width: auto;

    &.is-loading {
      opacity: .25;
    }
  }

  &-day {
    border-top: 1px solid $color-border;
    display: block;
    margin: 0 -2.6rem;
    padding: 0 2.6rem;

    &-label {
      display: block;
      font-size: 1.8rem;
      font-weight: 600;
      padding: 2.6rem 0;

      .counter {
        position: relative;
        top: -2px;
      }
    }
  }

  .todays-filtered-reservations-empty-state {
    color: $color-text-light;
    display: block;
    padding: 4rem 0;
    text-align: center;
  }

  .filters {
    display: flex;
    margin-bottom: 1rem;
    margin-top: -4.3rem;
    padding: 2rem 0;
    width: 100%;

    .button {
      width: 16rem;
    }

    select {
      height: 3.6rem;
      line-height: 3.6rem;
      min-width: 24rem;
      width: 24rem;
    }

    input {
      background: $color-light url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill-rule="evenodd" fill="%237F8FA4" d="M382.217,267.717a1,1,0,0,1-1.414,0l-3.15-3.15a7,7,0,1,1,1.415-1.414l3.149,3.15A1,1,0,0,1,382.217,267.717Zm-5.2-12.271a5,5,0,1,0,0,7.071A5,5,0,0,0,377.017,255.446Z" transform="translate(-366.5 -252)"/></svg>');
      background-repeat: no-repeat;
      background-position: calc(100% - 1.2rem) center;
      border: 1px solid $color-border;
      border-radius: $border-radius-small;
      height: 3.6rem;
      padding: 0 1rem;
      width: 100%;
    }

    .separator {
      display: block;
      height: 3.2rem;
      position: relative;
      min-width: 2.5rem;
      width: 2.5rem;

      &:before {
        background: $color-text-lightest;
        content: "";
        height: 24px;
        left: 12px;
        position: absolute;
        top: 6px;
        width: 1px;
      }
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all .25s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: scale(.5);
}

@media (max-width: 1200px) {
  .week-reservations .week-reservations-days-item {
    padding: 1rem .5rem;

    .counter {
      margin-left: .25rem;
    }
  }
}

@media (max-width: 1040px) {
  .week-reservations {
    &-days {
      border-bottom: 1px solid $color-border;
      margin-bottom: 4rem!important;

      .select-days-desktop {
        display: none;
      }

      .select-days-mobile {
        align-items: center;
        display: flex;
        margin: 0 auto;
        max-width: 480px;
        padding: 1rem 3rem;
        width: 100%;

        label {
          padding-right: 1rem;
          width: 40%;
        }

        select {
          width: 60%;
        }
      }
    }
    .filters {
      background: $color-bg-medium;
      flex-wrap: wrap;
      margin: -2.6rem -1.5rem 2rem -1.5rem;
      padding: 1rem 1.5rem 2rem 1.5rem;
      width: calc(100% + 3rem);

      & > .separator {
        display: none;
      }

      & > select,
      & > input,
      & > .button {
        margin-bottom: 1rem;
        width: 100%;
      }

      & > .button {
        margin: 0;
      }
    }

    .week-reservations-list {
      margin: 0 -1.25rem 1rem -1.25rem;

      .reservation {
        margin: 0 1.25rem 2.5rem 1.25rem;
        max-width: calc(50% - 2.5rem);
      }
    }
  }
}

@media (max-width: 680px) {
  .week-reservations {
    .week-reservations-block {
      padding: 2.5rem 1.5rem 0 1.5rem;
    }

    .week-reservations-days {
      margin: 0 -1.5rem;
    }

    .week-reservations-day-label {
      font-size: 1.5rem;

      .counter {
        top: 0;
      }
    }

    .week-reservations-day {
      margin: 0 -1.5rem;
      padding: 0 1.5rem;
    }

    .week-reservations-list {
      margin: 0 0 1rem 0;

      .reservation {
        margin: 0 0 2.5rem 0;
        max-width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/DashboardWeekReservations.vue